import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { CommonModule } from '@angular/common';
import {
  SVG_CAPTAIN,
  SVG_MEMBER,
  CAPTAIN_PIN,
  MEMBER_PIN,
} from '../../../../helpers/constants';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { MarkerService } from '../../../../services/marker.service';

@Component({
  selector: 'app-tracking-map',
  standalone: true,
  imports: [GoogleMapsModule, CommonModule],
  templateUrl: './tracking-map.component.html',
  styleUrl: './tracking-map.component.css',
})
export class TrackingMapComponent implements OnInit, OnDestroy {
  @ViewChild('map', { static: false }) map!: GoogleMap;

  $captainMarker!: Observable<any>;
  $memberLocation!: Observable<any>;
  $bounds!: Observable<any>;

  captainImg!: any;
  memberImg!: any;
  captainPin: string = CAPTAIN_PIN;
  memberPin: string = MEMBER_PIN;

  oldZoom!: any;
  removeGoogleZoomListener!: any;

  memberMarkerTitle: string = 'Your Location';
  captainMarkerTitle: string = 'Your Captain';

  SINGLE_MARKER_ZOOM: number = 15;

  options: google.maps.MapOptions = {
    mapId: 'DEMO_MAP_ID',
    gestureHandling: 'greedy',
  };

  private ngUnsubscribe = new Subject<void>();

  constructor(private markerService: MarkerService) {
    this.$captainMarker = this.markerService.$captainMarker;
    this.$memberLocation = this.markerService.$memberLocation;
    this.$bounds = this.markerService.$bounds;
  }

  ngOnInit(): void {
    const parser = new DOMParser();

    this.captainImg = parser.parseFromString(
      SVG_CAPTAIN,
      'image/svg+xml',
    ).documentElement;
    this.memberImg = parser.parseFromString(
      SVG_MEMBER,
      'image/svg+xml',
    ).documentElement;

    this.$bounds
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(() => !!this.map),
        map((bounds) => {
          return this.mapUpdate(bounds);
        }),
      )
      .subscribe({
        next: (bounds) => {
          console.log('[BOUNDS]: From setBounds()', bounds);
        },
        error: (error) =>
          console.error('[BOUNDS]: Error in setBounds()', error),
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

    google.maps.event.removeListener(this.removeGoogleZoomListener);
  }

  getMap(map: google.maps.Map) {
    console.log('[MAP]: Map initialized', map);
    // Force initial bounds calculation after map is ready
    this.$bounds
      .pipe(
        take(1),
        filter((bounds) => bounds && bounds.length > 0),
      )
      .subscribe((bounds) => {
        console.log('[MAP INIT]: Setting initial bounds', bounds);
        this.setBoundsInitOnMap(bounds);
      });
  }

  private async setBoundsInitOnMap(bounds: any[]) {
    if (!this.map?.googleMap) return;

    const googleMap = this.map.googleMap;

    await this.ensureGoogleMapsLoaded();

    const latLngBounds = new google.maps.LatLngBounds();

    for (let bound of bounds) {
      const canMakeMarker: boolean = Boolean(
        bound?.lat && (bound?.lng || bound?.lon),
      );
      if (canMakeMarker) {
        latLngBounds.extend({
          lat: bound.lat,
          lng: bound.lng || bound.lon,
        });
      }
    }

    //Check Number of Markers (1 || 2) -> determines map focus(centered || fitbounds) -> If centered Manually Set the zoom level -> fitBounds, zoom automatically gets calculated
    const adjustMap =
      bounds.length === 1
        ? () => {
            googleMap.setCenter(latLngBounds.getCenter());
            googleMap.setZoom(this.SINGLE_MARKER_ZOOM);
          }
        : () => googleMap.fitBounds(latLngBounds);

    adjustMap();
  }

  private async mapUpdate(bounds: any) {
    await this.ensureGoogleMapsLoaded();

    await this.setBoundsInitOnMap(bounds);

    if (this.map?.googleMap) {
      this.removeGoogleZoomListener = this.map.googleMap.addListener(
        'zoom_changed',
        () => (this.oldZoom = this.map.googleMap?.getZoom()),
      );
    }
  
    return bounds;
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    return new Promise<void>((res, _) => {
      const checkGoogleMaps = () => {
        const isReady: boolean = !!google?.maps?.LatLngBounds;
        return isReady ? res() : setTimeout(checkGoogleMaps, 50);
      };
      checkGoogleMaps();
    });
  }
}
